import { defineComponent, computed, onMounted, ref, watchEffect } from 'vue';
import { round } from 'lodash-es';
import { useLocaleReceiver } from '../locale-provider/LocaleReceiver';
import { withInstall } from '../_util/type';
export const numberAnimationProps = {
    to: {
        type: Number,
        default: 0
    },
    precision: {
        type: Number,
        default: 0
    },
    showSeparator: Boolean,
    locale: String,
    from: { type: Number, default: 0 },
    active: {
        type: Boolean,
        default: true
    },
    duration: {
        type: Number,
        default: 2000
    },
    onFinish: Function
};
const easeOut = (t) => 1 - Math.pow(1 - t, 5);
function tween(props) {
    const { from, to, duration, onUpdate, onFinish } = props;
    const tick = () => {
        const current = performance.now();
        const elapsedTime = Math.min(current - startTime, duration);
        const currentValue = from + (to - from) * easeOut(elapsedTime / duration);
        if (elapsedTime === duration) {
            onFinish();
            return;
        }
        onUpdate(currentValue);
        requestAnimationFrame(tick);
    };
    const startTime = performance.now();
    tick();
}
const NumberAnimation = defineComponent({
    name: 'PfNumberAnimation',
    props: numberAnimationProps,
    setup(props) {
        const [mergedLocale] = useLocaleReceiver(null);
        const { duration } = props;
        const displayedValueRef = ref(props.from);
        const mergedLocaleRef = computed(() => {
            const { locale } = props;
            if (locale !== undefined)
                return locale;
            return mergedLocale.value.lang;
        });
        let animating = false;
        const onUpdate = (currentValue) => {
            displayedValueRef.value = currentValue;
        };
        const onFinish = () => {
            displayedValueRef.value = props.to;
            animating = false;
            props.onFinish?.();
        };
        const animate = (from = props.from, to = props.to) => {
            animating = true;
            displayedValueRef.value = props.from;
            if (from !== to) {
                tween({
                    from,
                    to,
                    duration,
                    onUpdate,
                    onFinish
                });
            }
        };
        const formattedValueRef = computed(() => {
            const formatted = round(displayedValueRef.value, props.precision).toFixed(props.precision);
            const splitValue = formatted.split('.');
            const numberFormatter = new Intl.NumberFormat(mergedLocaleRef.value);
            const decimalSeparator = numberFormatter
                .formatToParts(0.5)
                .find((part) => part.type === 'decimal')?.value;
            const integer = props.showSeparator
                ? numberFormatter.format(Number(splitValue[0]))
                : splitValue[0];
            const decimal = splitValue[1];
            return {
                integer,
                decimal,
                decimalSeparator
            };
        });
        function play() {
            if (animating)
                return;
            animate();
        }
        onMounted(() => {
            watchEffect(() => {
                if (props.active)
                    animate();
            });
        });
        const exposedMethods = { play };
        return {
            formattedValue: formattedValueRef,
            ...exposedMethods
        };
    },
    render() {
        const { formattedValue: { integer, decimal, decimalSeparator } } = this;
        return [integer, decimal ? decimalSeparator : null, decimal];
    }
});
export default withInstall(NumberAnimation);
